import { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchQuotes = useCallback(async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const { data } = await axios.get(`${API}/admin/quotes`, { params });
      setQuotes(data);
    } catch (err) {
      console.error('Failed to load quotes:', err);
      toast.error('Failed to load quotes');
    }
  }, [filterStatus]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status });
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (err) {
      console.error('Failed to update quote status:', err);
      toast.error('Failed to update');
    }
  };

  const statusColors = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500' };

  return (
    <div data-testid="admin-quotes">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Quote Requests</h1>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="quote-filter"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-sm border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Product</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {quotes.map(q => (
              <TableRow key={q.id}>
                <TableCell className="text-xs">{new Date(q.created_at).toLocaleDateString('en-NZ')}</TableCell>
                <TableCell className="font-medium">{q.name}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{q.quote_type}</Badge></TableCell>
                <TableCell className="text-sm">{q.product_name || '-'}</TableCell>
                <TableCell className="text-sm">{q.email}</TableCell>
                <TableCell className="text-sm">{q.phone}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${statusColors[q.status] || ''}`}>{q.status}</span></TableCell>
                <TableCell>
                  <Select value={q.status} onValueChange={v => updateStatus(q.id, v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid={`quote-status-${q.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {quotes.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">No quote requests yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
